import type {
  AutomaticWebsiteAssessment,
  LibraryStatus,
  ProspectPriority,
  ProspectStatus,
  WebsiteQuality,
  WebsiteType,
} from '../types';

export const WEBSITE_TYPE_LABELS: Record<WebsiteType, string> = {
  NONE: 'Aucun site',
  SOCIAL: 'Reseau social uniquement',
  PLATFORM: 'Plateforme tierce',
  OWN_WEBSITE: 'Site proprietaire',
};

/** Version courte pour les cellules de tableau. */
export const WEBSITE_TYPE_SHORT_LABELS: Record<WebsiteType, string> = {
  NONE: 'Aucun site',
  SOCIAL: 'Reseau social',
  PLATFORM: 'Plateforme',
  OWN_WEBSITE: 'Site propre',
};

export const WEBSITE_QUALITY_LABELS: Record<WebsiteQuality, string> = {
  UNKNOWN: 'A analyser',
  GOOD: 'Bon site',
  BAD: 'Mauvais site',
};

/** Ordre du choix humain : du meilleur prospect au moins interessant. */
export const WEBSITE_QUALITY_ORDER: WebsiteQuality[] = ['BAD', 'GOOD', 'UNKNOWN'];

export const PRIORITY_LABELS: Record<ProspectPriority, string> = {
  HIGH: 'Prioritaire',
  MEDIUM: 'Interessant',
  LOW: 'Faible priorite',
};

export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  TO_CONTACT: 'A contacter',
  CONTACTED: 'Contacte',
  FOLLOW_UP: 'A relancer',
  INTERESTED: 'Interesse',
  APPOINTMENT_SET: 'RDV fixe',
  QUOTE_SENT: 'Devis envoye',
  WON: 'Gagne',
  NOT_INTERESTED: 'Pas interesse',
};

/**
 * Workflow V3 : trois etats au quotidien. Les statuts herites de la V2 restent
 * lisibles et selectionnables, mais seulement pour une fiche qui les porte
 * deja — voir statusOptions().
 */
export const PRIMARY_PROSPECT_STATUSES: ProspectStatus[] = [
  'TO_CONTACT',
  'FOLLOW_UP',
  'APPOINTMENT_SET',
];

export const LEGACY_PROSPECT_STATUSES: ProspectStatus[] = [
  'CONTACTED',
  'INTERESTED',
  'QUOTE_SENT',
  'WON',
  'NOT_INTERESTED',
];

/**
 * Choix proposes pour une fiche donnee : les trois etats V3, plus l'ancien
 * statut de la fiche s'il en porte un. Un prospect V2 reste donc lisible et
 * n'est jamais reecrit silencieusement.
 */
export function statusOptions(current: ProspectStatus): ProspectStatus[] {
  if (PRIMARY_PROSPECT_STATUSES.includes(current)) return PRIMARY_PROSPECT_STATUSES;
  return [...PRIMARY_PROSPECT_STATUSES, current];
}

/** Ordre d'affichage de reference : les trois du quotidien, puis les herites. */
export const PROSPECT_STATUS_ORDER: ProspectStatus[] = [
  ...PRIMARY_PROSPECT_STATUSES,
  ...LEGACY_PROSPECT_STATUSES,
];

/**
 * Statuts a proposer dans le filtre de la bibliotheque.
 *
 * Les trois du quotidien sont toujours la — filtrer sur "A relancer" doit
 * rester possible meme quand aucune fiche ne l'est encore, sinon l'option
 * apparait et disparait au fil du travail. Un statut herite de la V2 n'apparait
 * en revanche que si une fiche le porte reellement : sinon il ne peut renvoyer
 * que zero resultat, et cinq entrees mortes noient les trois utiles.
 */
export function statusFilterOptions(
  prospects: readonly { status: ProspectStatus }[],
): ProspectStatus[] {
  const portes = new Set(prospects.map((prospect) => prospect.status));
  return PROSPECT_STATUS_ORDER.filter(
    (status) => PRIMARY_PROSPECT_STATUSES.includes(status) || portes.has(status),
  );
}

/**
 * Avis technique automatique. Le vocabulaire reste prudent — « probable »,
 * « a verifier » — parce que PageSpeed ne juge que la technique : il ne voit ni
 * l'apparence du site, ni son contenu, ni l'interet commercial d'une refonte.
 */
export const ASSESSMENT_LABELS: Record<AutomaticWebsiteAssessment, string> = {
  LIKELY_BAD: 'Mauvais site probable',
  TO_CHECK: 'A verifier',
  NOT_ANALYZED: 'Non analyse',
  TECHNICALLY_OK: 'Techniquement correct',
};

export function assessmentTone(assessment: AutomaticWebsiteAssessment): string {
  switch (assessment) {
    case 'LIKELY_BAD':
      return 'badge-opp-4';
    case 'TO_CHECK':
      return 'badge-opp-2';
    case 'TECHNICALLY_OK':
      return 'badge-opp-0';
    default:
      return 'badge-neutral';
  }
}

/**
 * Ordre de traitement de la file « A traiter ».
 *
 * Un site non analyse passe **avant** un site techniquement correct : il reste
 * a qualifier, alors que le second a deja livre son verdict.
 */
export const ASSESSMENT_RANK: Record<AutomaticWebsiteAssessment, number> = {
  LIKELY_BAD: 0,
  TO_CHECK: 1,
  NOT_ANALYZED: 2,
  TECHNICALLY_OK: 3,
};

export const LIBRARY_STATUS_LABELS: Record<LibraryStatus, string> = {
  UNSAVED: 'Non sauvegarde',
  SAVED: 'Deja dans la bibliotheque',
  IGNORED: 'Ignore',
  TREATED: 'Deja traite',
  CONFIRMED: 'Rendez-vous fixe',
};

/**
 * Rampe d'opportunite : plus l'entreprise est mal equipee en web, plus le
 * badge est chaud. C'est le seul endroit ou la couleur chaude est utilisee,
 * ce qui lui garde toute sa force de signal.
 *
 * V3 : un site proprietaire juge mauvais est desormais le meilleur signal —
 * le badge suit donc l'evaluation humaine, pas seulement le type d'URL.
 */
export function websiteTypeTone(type: WebsiteType, quality: WebsiteQuality = 'UNKNOWN'): string {
  if (type === 'OWN_WEBSITE') {
    switch (quality) {
      case 'BAD':
        return 'badge-opp-4';
      case 'GOOD':
      case 'UNKNOWN':
      default:
        return 'badge-opp-0';
    }
  }

  switch (type) {
    case 'NONE':
      return 'badge-opp-3';
    case 'SOCIAL':
      return 'badge-opp-2';
    case 'PLATFORM':
      return 'badge-opp-1';
    default:
      return 'badge-opp-0';
  }
}

/** Libelle web affiche : le jugement humain prime sur le simple type d'URL. */
export function websiteLabel(type: WebsiteType, quality: WebsiteQuality): string {
  if (type !== 'OWN_WEBSITE') return WEBSITE_TYPE_SHORT_LABELS[type];
  if (quality === 'UNKNOWN') return 'Site a analyser';
  return WEBSITE_QUALITY_LABELS[quality];
}

/** Le score utilise une forme distincte du badge : un nombre, pas une pilule. */
export function priorityTone(priority: ProspectPriority): string {
  switch (priority) {
    case 'HIGH':
      return 'score-high';
    case 'MEDIUM':
      return 'score-medium';
    case 'LOW':
      return 'score-low';
  }
}

/**
 * Les statuts commerciaux restent majoritairement neutres : seuls les points
 * marquants du cycle meritent une couleur, pour ne pas diluer le signal.
 */
export function prospectStatusTone(status: ProspectStatus): string {
  switch (status) {
    case 'WON':
      return 'badge-success';
    case 'APPOINTMENT_SET':
      return 'badge-success';
    case 'TO_CONTACT':
      return 'badge-accent';
    default:
      return 'badge-neutral';
  }
}

export function libraryStatusTone(status: LibraryStatus): string {
  switch (status) {
    case 'SAVED':
      return 'badge-accent';
    case 'CONFIRMED':
      return 'badge-success';
    case 'TREATED':
      return 'badge-treated';
    default:
      return 'badge-neutral';
  }
}
